import { useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../lib/api';
import { AlertBanner } from '../../components/ui/AlertBanner';

/**
 * "No Apify API key set", for the Add source dialog and the Sources page. Each caller decides WHEN
 * it shows; this only checks there is genuinely no fetch path - the server folds the site key and
 * the deployment's APIFY_TOKEN fallback into `apifyConfigured`. `=== false` (not `!truthy`) so an
 * older payload without the field never false-flashes.
 *
 * Site Integrations is Tenant-Admin-only: anyone else is told who can add the key, with no link to
 * a page that would only say "no access". `newTab` keeps the page behind it, e.g. a half-filled form.
 *
 * A key added in another tab (this link's, or an admin's elsewhere) does not reach this one, so
 * while the warning is up, coming back to the tab asks /auth/me again and merges the answer in.
 */
export function ApifyKeyWarning({ newTab = false }: { newTab?: boolean }) {
  const { principal, updateUser } = useAuth();
  const user = principal?.user;
  const missing = user?.apifyConfigured === false;

  useEffect(() => {
    if (!missing) return;
    // One question at a time: a newer one (or leaving) cancels the last, so a slow, older answer
    // never lands after a newer one.
    let inFlight: AbortController | undefined;
    const recheck = () => {
      if (document.visibilityState === 'hidden') return;
      inFlight?.abort();
      const asking = new AbortController();
      inFlight = asking;
      api
        .me(asking.signal)
        .then((me) => {
          if (!asking.signal.aborted && me.user.apifyConfigured !== false) updateUser(me.user);
        })
        // The warning just stays up; the next return to the tab asks again.
        .catch(() => undefined);
    };
    // Both: not every way back into a tab fires the window's focus.
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
      inFlight?.abort();
    };
  }, [missing, updateUser]);

  if (!user || !missing) return null;
  return user.isTenantAdmin ? (
    <AlertBanner tone="warning" action={{ label: 'Add key', to: '/site-integrations?tab=apify', newTab }}>
      No Apify API key set — your Digest Sources can&apos;t fetch new content.
    </AlertBanner>
  ) : (
    <AlertBanner tone="warning">
      No Apify API key set — your Digest Sources can&apos;t fetch new content. Ask a Tenant Admin to
      add one in Site Integrations.
    </AlertBanner>
  );
}
